
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { MoreHorizontal, Edit, Trash2, Users } from "lucide-react";
import { CustomRole, useRolePermissions, useDeleteRole } from '@/hooks/useRoles';
import { useToast } from "@/hooks/use-toast";

interface RoleCardProps {
  role: CustomRole;
  onEdit: (role: CustomRole) => void;
  onAssign: (role: CustomRole) => void;
}

export function RoleCard({ role, onEdit, onAssign }: RoleCardProps) {
  const { data: permissions } = useRolePermissions(role.id);
  const deleteRole = useDeleteRole();
  const { toast } = useToast();
  const enabledPermissions = permissions?.filter(p => p.permission_value).length || 0;

  const handleDelete = async () => {
    if (window.confirm(`هل أنت متأكد من حذف الدور "${role.name}"؟`)) {
      try {
        await deleteRole.mutateAsync(role.id);
        toast({
          title: "تم الحذف",
          description: "تم حذف الدور بنجاح",
        });
      } catch (error) {
        console.error('Error deleting role:', error);
        toast({
          title: "خطأ",
          description: "حدث خطأ أثناء حذف الدور",
          variant: "destructive",
        });
      }
    }
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex justify-between items-start">
            <div className="flex items-center gap-3">
                <div 
                    className="w-4 h-4 rounded-full border shrink-0" 
                    style={{ backgroundColor: role.color }}
                />
                <CardTitle className="text-lg">{role.name}</CardTitle>
            </div>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="h-8 w-8 p-0">
                  <MoreHorizontal className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => onEdit(role)}>
                  <Edit className="mr-2 h-4 w-4" />
                  تعديل
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => onAssign(role)}>
                  <Users className="mr-2 h-4 w-4" />
                  تعيين للموظفين
                </DropdownMenuItem>
                <DropdownMenuItem 
                  onClick={handleDelete}
                  className="text-red-600 hover:text-red-700 hover:bg-red-50"
                >
                  <Trash2 className="mr-2 h-4 w-4" />
                  حذف
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
        </div>
        <p className="text-sm text-gray-500 pt-2">{role.description || 'لا يوجد وصف'}</p>
      </CardHeader>
      <CardContent>
        <div className="flex justify-between items-center">
            <Badge variant="outline">{enabledPermissions} صلاحية</Badge>
            <p className="text-xs text-gray-500">
                أنشئ في: {new Date(role.created_at).toLocaleDateString('ar-SA')}
            </p>
        </div>
      </CardContent>
    </Card>
  );
}
